import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  api,
  parseApiError,
  type BillingInterval,
  type BillingPlan,
  type EntitlementStatus,
} from '../lib/api';
import { cn } from '../lib/cn';
import { Card } from './Card';
import { MORPH } from '../lib/motion';
import { Crown, ShieldCheck } from 'lucide-react';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

// Moved out of Organizations.tsx (org-step-segmentation consolidation) —
// founder-only entitlement/plan/interval override. Mounted only inside
// TournamentSetup's Organisation step, Founder Settings section.
export function FounderBillingControls({
  isFounder,
  orgId,
  onDone,
}: {
  isFounder: boolean;
  orgId: string;
  onDone: () => void;
}) {
  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus>('ACTIVE');
  const [plan, setPlan] = useState<BillingPlan>('CUSTOM');
  const [interval, setInterval] = useState<BillingInterval>('PER_COMPETITION');
  const [error, setError] = useState('');
  const mut = useMutation({
    mutationFn: () => api.admin.setOrgBilling(orgId, { entitlementStatus, plan, interval }),
    onSuccess: () => {
      setError('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Defense in depth — the host section is already founder-gated.
  if (!isFounder) return null;

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <Crown className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Founder Billing Override</h2>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <select className="input" value={entitlementStatus} onChange={e => setEntitlementStatus(e.target.value as EntitlementStatus)}>
          <option value="ACTIVE">Active</option>
          <option value="TRIALING">Trialing</option>
          <option value="GRACE_PERIOD">Grace period</option>
          <option value="PAST_DUE">Past due</option>
          <option value="CANCELED">Canceled</option>
          <option value="INACTIVE">Inactive</option>
        </select>
        <select className="input" value={plan} onChange={e => setPlan(e.target.value as BillingPlan)}>
          <option value="STARTER">Starter</option>
          <option value="GROWTH">Growth</option>
          <option value="PREMIER">Premier</option>
          <option value="CUSTOM">Custom</option>
        </select>
        <select className="input" value={interval} onChange={e => setInterval(e.target.value as BillingInterval)}>
          <option value="PER_COMPETITION">Per competition</option>
          <option value="MONTHLY">Monthly</option>
          <option value="ANNUAL">Annual</option>
        </select>
      </div>
      <button className={cn('btn-primary mt-4 inline-flex items-center gap-2', MORPH)} onClick={() => mut.mutate()} disabled={mut.isPending}>
        <ShieldCheck className="h-4 w-4" />
        {mut.isPending ? 'Updating…' : 'Apply Override'}
      </button>
      <ErrorMsg msg={error} />
    </Card>
  );
}
